import React, { useEffect, useState, useMemo } from 'react';
import { Card, DataTable, Link, Page, Badge, BlockStack, Text, Box } from '@shopify/polaris';
import userRoles from '../../utils/userRoles';
import { useAuthStore } from '../../context/useStore';
import { getRequest } from '../../shared/components/functions';
import { getDocPdfLinkForListView } from '../../shared/components/functions';

export default function RoasthubsPage() {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  if (user?.role !== userRoles.AdminFull) return null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getRequest('/api/roasthubs');
        setItems(data || []);
      } catch (err) {
        console.error('Error fetching roasthubs data:', err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group items by year
  const itemsByYear = useMemo(() => {
    const grouped = {};
    items.forEach((item) => {
      if (item.invoice_date) {
        const year = new Date(item.invoice_date).getFullYear();
        if (!grouped[year]) {
          grouped[year] = [];
        }
        grouped[year].push(item);
      }
    });

    // Sort years in descending order
    const sortedYears = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));
    const sorted = {};
    sortedYears.forEach((year) => {
      sorted[year] = grouped[year];
    });

    return sorted;
  }, [items]);

  const getDaysUntilNextBill = (invoiceDate) => {
    if (!invoiceDate) return null;

    const invoice = new Date(invoiceDate);
    const nextBillDate = new Date(invoice);
    nextBillDate.setFullYear(nextBillDate.getFullYear() + 1); // Add 1 year for annual billing

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextBillDate.setHours(0, 0, 0, 0);

    const diffTime = nextBillDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const getNextBillBadge = (daysUntil) => {
    if (daysUntil === null || daysUntil === undefined) return null;

    let tone = 'success';
    let text = `${daysUntil} days`;

    if (daysUntil < 0) {
      tone = 'critical';
      text = `${Math.abs(daysUntil)} days overdue`;
    } else if (daysUntil <= 30) {
      tone = 'attention';
      text = `${daysUntil} days`;
    } else if (daysUntil <= 60) {
      tone = 'warning';
      text = `${daysUntil} days`;
    }

    return <Badge tone={tone}>{text}</Badge>;
  };

  const renderTableForYear = (year, yearItems) => {
    const rows = yearItems.map((item) => {
      const invoiceDate = item.invoice_date ? String(item.invoice_date).substring(0, 10) : '';
      const invoiceNumber = item.Factuurnummer || '';
      const invoiceLink = item.faktuurnr ? `/invoices/${item.faktuurnr}` : null;
      const description = item.tekstlijn || '';
      const customerName = item.customer_name || '';
      const customerLink = item.customer_id ? `/companies/${item.customer_id}` : null;
      const value = item.line_value ? Number(item.line_value) : 0;
      const pdfLink = getDocPdfLinkForListView(item, 'invoice', item.companyNumber, invoiceNumber);
      const daysUntilNextBill = getDaysUntilNextBill(item.invoice_date);
      const nextBillBadge = getNextBillBadge(daysUntilNextBill);

      return [
        invoiceLink ? (
          <Link removeUnderline url={invoiceLink}>
            {invoiceNumber}
          </Link>
        ) : (
          invoiceNumber
        ),
        pdfLink || '',
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {invoiceDate}
          {nextBillBadge}
        </div>,
        description,
        customerLink ? (
          <Link removeUnderline url={customerLink}>
            {customerName}
          </Link>
        ) : (
          customerName
        ),
        `€${value.toFixed(2)}`
      ];
    });

    const yearTotal = yearItems.reduce((sum, item) => sum + (item.line_value || 0), 0);
    const totals = [<b>Total {year}</b>, '', '', '', '', <b>{`€${yearTotal.toFixed(2)}`}</b>];

    return (
      <Box key={year} paddingBlockEnd="400">
        <Card>
          <Box paddingBlockStart="300" paddingBlockEnd="300">
            <Text variant="headingMd" as="h2">
              {year}
            </Text>
          </Box>
          <DataTable
            verticalAlign="middle"
            stickyHeader={true}
            increasedTableDensity={true}
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
            headings={[<b>{'Invoice Number'}</b>, <b>{'PDF'}</b>, <b>{'Date'}</b>, <b>{'Description'}</b>, <b>{'Customer'}</b>, <b>{'Value'}</b>]}
            rows={rows}
            totals={totals}
            showTotalsInFooter
          />
        </Card>
      </Box>
    );
  };

  return (
    <Page title="Roasthubs" fullWidth>
      <BlockStack gap="400">
        {loading ? (
          <Card>
            <div>Loading...</div>
          </Card>
        ) : Object.keys(itemsByYear).length > 0 ? (
          Object.keys(itemsByYear).map((year) => renderTableForYear(year, itemsByYear[year]))
        ) : (
          <Card>
            <div>No roasthubs invoice lines found</div>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
